import type { SlateBlock, SlateLeaf, TableBlock } from '@atlas/renderer';
import { Editor, Element, Transforms, type BaseSelection } from 'slate';
import extractItemsFromListBlock from '@atlas/tool/box/slate/extractItemsFromListBlock';
import isCalloutBlock from '@atlas/tool/box/slate/isCalloutBlock';
import isListBlock from '@atlas/tool/box/slate/isListBlock';
import isMultiLineTextBlock, { type MultiLineTextBlock } from '@atlas/tool/box/slate/isMultiLineTextBlock';
import isSameLeafFormat from '@atlas/tool/box/slate/isSameLeafFormat';
import isTextBlock from '@atlas/tool/box/slate/isTextBlock';
import matchTopBlockNodes from '@atlas/tool/box/slate/matchTopBlockNodes';
import removeTopLevelBlocks from '@atlas/tool/box/slate/removeTopLevelBlocks';
import sortSelection from '@atlas/tool/box/slate/sortSelection';
import changeEditorSelection from '@atlas/tool/box/slate/changeEditorSelection';
import isTableBlock from '@atlas/tool/box/slate/isTableBlock';

export default function convertBlocksToMultiLineTexts(
    editor: Editor,
    blockType: MultiLineTextBlock['type'] | 'paragraph',
    options?: {
        match?: SlateBlock['type'] | ((node: SlateBlock) => boolean);
        range?: BaseSelection;
    },
) {
    const selection = options?.range || editor.selection;
    if (!selection) return;

    const { anchor, focus } = selection;
    const [selectionStart, selectionEnd] = sortSelection({ anchor, focus });

    const match = options?.match;
    const matches = matchTopBlockNodes(editor, {
        at: selection,
    });

    const allBlocks = matches.reduce<SlateBlock[]>((allBlocks, [block]) => {
        if (typeof match === 'string' && match !== block.type) {
            return allBlocks.concat(block);
        }

        if (typeof match === 'function' && !match(block)) {
            return allBlocks.concat(block);
        }

        let leaves: SlateLeaf[] | null = null;

        if (isCalloutBlock(block)) {
            block = block.children[1];
        }

        if (isTextBlock(block)) {
            leaves = block.children;
        }

        if (isListBlock(block)) {
            leaves = extractItemsFromListBlock(block)
                .map((listItem) => listItem.children)
                .reduce((allLeaves, [first, ...more]) => {
                    const lastLeaf = allLeaves[allLeaves.length - 1];
                    if (!isSameLeafFormat(lastLeaf, first)) {
                        return allLeaves.concat({ ...first, text: '\n' + first.text }).concat(more);
                    }
                    return allLeaves
                        .slice(0, -1)
                        .concat({ ...lastLeaf, text: lastLeaf.text + '\n' + first.text })
                        .concat(more);
                });
        }

        if (isTableBlock(block)) {
            leaves = transformTableToText(block);
        }

        if (!leaves) return allBlocks.concat(block);

        const lastBlock = allBlocks[allBlocks.length - 1];
        const isLastBlockMultiline = !!lastBlock && isMultiLineTextBlock(lastBlock);

        if (!isLastBlockMultiline) {
            return allBlocks.concat({ type: blockType, children: leaves });
        }

        const [first, ...more] = leaves;
        const lastLeaf = lastBlock.children[lastBlock.children.length - 1];

        const newLeaves = isSameLeafFormat(lastLeaf, first)
            ? lastBlock.children
                  .slice(0, -1)
                  .concat({ ...lastLeaf, text: lastLeaf.text + '\n' + first.text })
                  .concat(more)
            : lastBlock.children.concat({ ...first, text: '\n' + first.text }).concat(more);

        allBlocks[allBlocks.length - 1] = {
            ...lastBlock,
            children: newLeaves,
        };

        return allBlocks;
    }, []);

    removeTopLevelBlocks(editor, selectionStart.path[0], selectionEnd.path[0]);
    Transforms.insertNodes(editor, allBlocks, { at: [selectionStart.path[0]] });

    const newSelectionStart = Editor.start(editor, [selectionStart.path[0]]);

    const newSelectionEnd = Editor.end(editor, [selectionStart.path[0] + allBlocks.length - 1]);

    changeEditorSelection(editor, {
        anchor: anchor === selectionStart ? newSelectionStart : newSelectionEnd,
        focus: anchor === selectionStart ? newSelectionEnd : newSelectionStart,
    });
}

const transformTableToText = (table: TableBlock) => {
    const rows = table.children;
    const leaves = rows
        .map((row, rowIndex) => {
            if (!Element.isElement(row)) return [];
            const rowContent = row.children.flatMap((cell, index) => {
                if (!Element.isElement(cell)) return [];
                const cellContent = cell.children;
                return index === 0 ? cellContent : [{ text: ' | ' }, ...cellContent];
            });
            return rowIndex === 0 ? rowContent : [{ text: '\n' }, ...rowContent];
        })
        .flat();

    return leaves;
};

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('convertBlocksToMultiLineTexts', async () => {
        it.todo('🤷🏼');
    });
}

// #endregion
